import { handleProfile } from '@ping/authorization/side-effects';
import * as configs from '@ping/configs';
import { tokenStore } from '@ping/stores/token.store';
import {
  AuthorizationNotifier,
  AuthorizationRequest,
  AuthorizationRequestHandler,
  AuthorizationResponse,
  AuthorizationServiceConfiguration,
  BaseTokenRequestHandler,
  EndSessionRequest,
  EndSessionResponse,
  FetchRequestor,
  GRANT_TYPE_AUTHORIZATION_CODE,
  GRANT_TYPE_REFRESH_TOKEN,
  RedirectRequestHandler,
  Requestor,
  StringMap,
  TokenRequest,
  TokenRequestHandler,
  TokenResponse,
} from '@ting/app-auth';

import { LOGIN_CALLBACK_PATH, LOGOUT_REDIRECTION_CALLBACK_PATH } from './constants';
import { QueryStringUtils } from './query-string-utils';

const { origin } = window.location;
const settings = {
  // URL to the authentication server (including realm)
  authority: configs.HYDRA_DOMAIN,

  // The name of the client in Hydra setup for this service
  client_id: configs.HYDRA_CLIENT,

  // Where to redirect the user to after successful authentication
  redirect_uri: `${origin}${LOGIN_CALLBACK_PATH}`,

  // Where to redirect the user to after logging the user out
  post_logout_redirect_uri: `${origin}${LOGOUT_REDIRECTION_CALLBACK_PATH}`,
  audience: configs.HYDRA_AUDIENCE,

  // "openid" tells the server that this client uses oidc for authentication
  scope: 'openid offline_access',
};

class Authorizer {
  private notifier: AuthorizationNotifier;
  private authorizationHandler: AuthorizationRequestHandler;
  private tokenHandler: TokenRequestHandler;

  // state
  private configuration: AuthorizationServiceConfiguration | undefined;
  private request: AuthorizationRequest | undefined;
  private code: string | undefined;
  private requestor: Requestor;
  private silentSigninInProgress = false;

  constructor() {
    this.notifier = new AuthorizationNotifier();
    this.authorizationHandler = new RedirectRequestHandler(undefined, new QueryStringUtils());
    this.requestor = new FetchRequestor();
    this.tokenHandler = new BaseTokenRequestHandler(this.requestor);
    // set notifier to deliver responses
    this.authorizationHandler.setAuthorizationNotifier(this.notifier);
    // set a listener to listen for authorization responses
    this.notifier.setAuthorizationListener((request, response) => {
      if (response && response instanceof AuthorizationResponse && request instanceof AuthorizationRequest) {
        this.request = request;
        this.code = response.code;
      }
      if (response && response instanceof EndSessionResponse) {
        tokenStore.revokeToken();
      }
    });
  }

  fetchServiceConfiguration() {
    return AuthorizationServiceConfiguration.fetchFromIssuer(settings.authority, this.requestor)
      .then(response => {
        this.configuration = response;
      })
      .catch(() => {
        // ignore
      });
  }

  async makeAuthorizationRequest() {
    const request = new AuthorizationRequest({
      client_id: settings.client_id,
      redirect_uri: settings.redirect_uri,
      scope: settings.scope,
      audience: settings.audience,
      response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
      extras: { prompt: 'consent', access_type: 'offline' },
    });

    if (this.configuration) {
      this.authorizationHandler.performAuthorizationRequest(this.configuration, request);
    } else {
      /** Try again till the configuration get ready */
      await this.fetchServiceConfiguration();
      setTimeout(() => this.makeAuthorizationRequest(), 1000);
    }
  }

  async makeTokenRequestFromCode() {
    if (!this.configuration || !this.code) {
      console.error('login_required');
    }

    let extras: StringMap | undefined;
    if (this.request && this.request.internal) {
      extras = {};
      extras.code_verifier = this.request.internal.code_verifier;
    }
    // use the code to make the token request.
    const request = new TokenRequest({
      client_id: settings.client_id,
      redirect_uri: settings.redirect_uri,
      grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
      code: this.code,
      refresh_token: undefined,
      extras,
    });

    try {
      const tokenResponse = await this.tokenHandler.performTokenRequest(this.configuration, request);
      this.handleTokenResponse(tokenResponse);
    } catch (error) {
      // do something
    }
  }

  async makeRefreshTokenRequest() {
    const currentTokenResponse = tokenStore.getTokenResponse();
    if (!this.configuration || !currentTokenResponse) {
      throw new Error('login_required');
    }

    // use the token response to make a request for an access token
    const request = new TokenRequest({
      client_id: settings.client_id,
      redirect_uri: settings.redirect_uri,
      grant_type: GRANT_TYPE_REFRESH_TOKEN,
      refresh_token: currentTokenResponse?.refreshToken,
      audience: settings.audience,
    });

    try {
      const newTokenResponse = await this.tokenHandler.performTokenRequest(this.configuration, request);
      this.handleTokenResponse(newTokenResponse);
      this.silentSigninInProgress = false;
      await handleProfile();
    } catch (error) {
      throw new Error('login_required');
    }
  }

  handleTokenResponse = (response: TokenResponse) => {
    tokenStore.setTokenResponse(response);

    // unset code, so we can do refresh token exchanges subsequently
    this.code = undefined;
  };

  checkForAuthorizationResponse() {
    return this.authorizationHandler.completeAuthorizationRequestIfPossible();
  }

  async signinCallback() {
    if (!this.configuration) {
      await this.fetchServiceConfiguration();
    }
    await this.checkForAuthorizationResponse();
    return this.makeTokenRequestFromCode();
  }

  timeout(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async silentSignin() {
    if (!this.configuration) {
      await this.fetchServiceConfiguration();
      /* ToDo: don't like to use dalay here ==> find better way */
      /* Do not trigger refresh request too fast if do it fast we may lose token along the way */
      await this.timeout(500);
    }
    if (!this.silentSigninInProgress) {
      this.silentSigninInProgress = true;
      return this.makeRefreshTokenRequest();
    }
    return Promise.resolve();
  }

  async signout() {
    if (!this.configuration) {
      await this.fetchServiceConfiguration();
    }
    const request = new EndSessionRequest({
      id_token_hint: tokenStore.getIdToken(),
      post_logout_redirect_uri: settings.post_logout_redirect_uri,
      state: undefined,
    });
    return this.authorizationHandler.performEndSessionRequest(this.configuration, request);
  }

  async signoutCallback() {
    if (!this.configuration) {
      await this.fetchServiceConfiguration();
    }
    await this.authorizationHandler.completeEndSessionRequestIfPossible();
  }
}

export const authorizer = new Authorizer();
export default authorizer;
